import { motion } from 'motion/react';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { staggerContainer, fadeInUp, pricingCardVariants } from '@/lib/animations';
import { useTranslation } from 'react-i18next';
import { CheckIcon } from '@heroicons/react/24/solid';

export function Pricing() {
  const { t } = useTranslation();
  const { ref, isInView } = useScrollAnimation();

  const plans = [
    {
      name: t('pricing.starter.name'),
      price: '$9.99',
      period: t('pricing.perMonth'),
      description: t('pricing.starter.description'),
      features: t('pricing.starter.features', { returnObjects: true }) as string[],
      highlighted: false,
    },
    {
      name: t('pricing.pro.name'),
      price: '$19.99',
      period: t('pricing.perMonth'),
      description: t('pricing.pro.description'),
      features: t('pricing.pro.features', { returnObjects: true }) as string[],
      highlighted: true,
      badge: t('pricing.pro.badge'),
    },
  ];

  return (
    <section id="pricing" className="py-16 md:py-24">
      <Container size="narrow">
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {/* Section header */}
          <motion.div variants={fadeInUp} className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
              {t('pricing.title')}{' '}
              <span className="text-gradient">{t('pricing.titleHighlight')}</span>
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-slate-400 max-w-2xl mx-auto">
              {t('pricing.subtitle')}
            </p>
          </motion.div>

          {/* Pricing cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-3xl mx-auto">
            {plans.map((plan, index) => (
              <motion.div
                key={index}
                variants={pricingCardVariants}
                whileHover={{ y: -8, transition: { duration: 0.2 } }}
                className={`relative rounded-2xl p-8 ${
                  plan.highlighted
                    ? 'bg-primary-600 text-white ring-4 ring-primary-600 ring-offset-4 ring-offset-white dark:ring-offset-slate-900'
                    : 'bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700'
                }`}
              >
                {/* Badge */}
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge variant="new">{plan.badge}</Badge>
                  </div>
                )}

                {/* Plan name */}
                <h3 className={`text-xl font-semibold mb-2 ${
                  plan.highlighted ? 'text-white' : 'text-gray-900 dark:text-white'
                }`}>
                  {plan.name}
                </h3>

                {/* Price */}
                <div className="flex items-baseline mb-4">
                  <span className={`text-5xl font-bold ${
                    plan.highlighted ? 'text-white' : 'text-gray-900 dark:text-white'
                  }`}>
                    {plan.price}
                  </span>
                  <span className={`ml-2 ${
                    plan.highlighted ? 'text-primary-100' : 'text-gray-500 dark:text-slate-400'
                  }`}>
                    {plan.period}
                  </span>
                </div>

                {/* Description */}
                <p className={`mb-6 ${
                  plan.highlighted ? 'text-primary-100' : 'text-gray-600 dark:text-slate-400'
                }`}>
                  {plan.description}
                </p>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        plan.highlighted
                          ? 'bg-primary-500'
                          : 'bg-primary-100 dark:bg-primary-900/30'
                      }`}>
                        <CheckIcon className={`w-3 h-3 ${
                          plan.highlighted ? 'text-white' : 'text-primary-600 dark:text-primary-400'
                        }`} />
                      </div>
                      <span className={
                        plan.highlighted ? 'text-primary-50' : 'text-gray-600 dark:text-slate-400'
                      }>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Button
                  variant={plan.highlighted ? 'secondary' : 'primary'}
                  className="w-full"
                  size="lg"
                  disabled
                >
                  {t('common.comingSoon')}
                </Button>
              </motion.div>
            ))}
          </div>

          {/* Additional info */}
          <motion.div variants={fadeInUp} className="mt-12 text-center space-y-2">
            <p className="text-xs text-gray-400 dark:text-slate-500">
              {t('pricing.fairUseNote')}{' '}
              <a href="/fair-use" className="text-primary-600 dark:text-primary-400 hover:underline">
                {t('pricing.fairUseLink')}
              </a>
              {t('pricing.fairUseDetails')}
            </p>
            <p className="text-sm text-gray-400 dark:text-slate-600">
              {t('pricing.customPlan')}{' '}
              <a href="mailto:hola@affogato.cloud" className="text-primary-600 dark:text-primary-400 hover:underline">
                {t('common.contactUs')}
              </a>
            </p>
          </motion.div>
        </motion.div>
      </Container>
    </section>
  );
}
